import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { deleteNotification, fetchNotificationsList } from "../../shared/organizationMastersApi";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

const dataGridSx = {
  border: "none",
  fontFamily: "inherit",
  fontSize: "0.9rem",
  height: "100%",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f2fcf5",
    borderBottom: "1px solid #e8ecea",
    fontWeight: 600
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #e8ecea",
    backgroundColor: "#fbfffc"
  },
  "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
    backgroundColor: "#fcfffd"
  },
  "& .MuiDataGrid-cell": {
    borderBottomColor: "#e8ecea"
  }
};

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

export function NotificationsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const userById = useMemo(() => {
    const m = new Map();
    for (const u of users) m.set(u.id, u);
    return m;
  }, [users]);

  const rows = useMemo(() => {
    return items.map((x) => {
      const user = userById.get(x.userId);
      return {
        id: x.id,
        notification: x,
        userName: user?.userName || (x.userId != null ? `User #${x.userId}` : "-")
      };
    });
  }, [items, userById]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [nRes, uRes] = await Promise.all([fetchNotificationsList(), apiGet("/api/Users")]);
    if (!nRes.ok) {
      setError(nRes.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(nRes.items);
    }
    if (uRes.ok) setUsers(uRes.data ?? []);
    setLoading(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (id) => {
      if (!(await window.confirmDialog("Delete this notification?"))) return;
      const res = await deleteNotification(id);
      if (!res.ok) setError(res.error ?? "Failed to delete notification");
      else await load();
    },
    [load]
  );

  const columns = useMemo(
    () => [
      {
        field: "userName",
        headerName: "User",
        flex: 0.9,
        minWidth: 120,
        valueGetter: (_v, row) => row.userName
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1,
        minWidth: 160,
        valueGetter: (_v, row) => row.notification.description ?? "-"
      },
      {
        field: "date",
        headerName: "Date",
        minWidth: 130,
        valueGetter: (_v, row) => formatDateTime(row.notification.date)
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const x = params.row.notification;
          return (
            <div className="table-actions">
              <button className="icon-button" type="button" title="Edit" onClick={() => navigate(`/notifications?edit=${x.id}`)}>
                ✏️
              </button>
              <button className="icon-button danger" type="button" title="Delete" onClick={() => void handleDeleteClick(x.id)}>
                🗑️
              </button>
            </div>
          );
        }
      }
    ],
    [navigate, handleDeleteClick]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Notifications</h2>
          <p>{loading ? "Loading notifications..." : `Total: ${total}`}</p>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={dataGridSx}
          />
        </div>
      </div>
    </div>
  );
}
